import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { dateTimeToMonDDYYYYWithTime } from '../utils/dateUtils';

const COMMENT_TYPES = [
  { label: 'General', value: 'General' },
  { label: 'Warning', value: 'Warning' },
  { label: 'Compliment', value: 'Compliment' },
  { label: 'Evaluation', value: 'Evaluation' },
];

const FLAG_COMMENT_TYPES = [
  { label: 'General', value: 'Flag-General' },
  { label: 'Flag', value: 'Flag' },
];

const ProjectComments = ({ referenceId, table, userData, isOpen, onClose, relevantRole, onEvaluationComment, defaultType = 'General' }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newComment, setNewComment] = useState('');
  const [newType, setNewType] = useState(defaultType);
  const [submitting, setSubmitting] = useState(false);

  const isStudent = userData && userData.accesscards && userData.default_accesscard_id &&
    userData.accesscards.find(ac => ac.accesscard_id === userData.default_accesscard_id)?.role === 'Student';

  // Check if this is a Flag interface
  const isFlagInterface = defaultType === 'Flag' || defaultType === 'Flag-General';

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setError('');
    axios.get(`/api/comments?reference_id=${referenceId}&table=${table}`)
      .then(res => {
        const allComments = res.data.comments || [];
        // If this is a Flag interface, only show Flag and Flag-General comments
        if (isFlagInterface) {
          const flagComments = allComments.filter(c => c.type === 'Flag' || c.type === 'Flag-General');
          setComments(flagComments);
          
          // If there are existing Flag comments and user is Admin, default to Flag-General
          const hasExistingFlags = flagComments.some(c => c.type === 'Flag');
          if (hasExistingFlags && relevantRole === 'Admin' && defaultType === 'Flag') {
            setNewType('Flag-General');
          }
        } else {
          // For regular comments, exclude Flag and Flag-General comments
          setComments(allComments.filter(c => c.type !== 'Flag' && c.type !== 'Flag-General'));
        }
      })
      .catch(() => setError('Failed to load comments'))
      .finally(() => setLoading(false));
  }, [isOpen, referenceId, table, isFlagInterface, relevantRole, defaultType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment || newComment === '<p><br></p>') return;
    setSubmitting(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/comments', {
        content: newComment,
        type: newType,
        reference_id: referenceId,
        table: table,
        account: userData.account_id || undefined,
        status: 'created',
        relevantRole: relevantRole || (isStudent ? 'Student' : 'Instructor'),
        ...(userData && !userData.account_id ? {
          alt_account: {
            first_name: userData.first_name,
            last_name: userData.last_name,
            primary_email: userData.primary_email
          }
        } : {})
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (newType === 'Evaluation' && typeof onEvaluationComment === 'function') {
        onEvaluationComment();
      }
      setNewComment('');
      setNewType(isFlagInterface ? 'Flag' : 'General');
      // Refresh comments
      const res = await axios.get(`/api/comments?reference_id=${referenceId}&table=${table}`);
      const allComments = res.data.comments || [];
      // If this is a Flag interface, only show Flag and Flag-General comments
      if (isFlagInterface) {
        setComments(allComments.filter(c => c.type === 'Flag' || c.type === 'Flag-General'));
      } else {
        // For regular comments, exclude Flag and Flag-General comments
        setComments(allComments.filter(c => c.type !== 'Flag' && c.type !== 'Flag-General'));
      }
    } catch (err) {
      setError('Failed to submit comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResolveFlag = async (commentId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/comments/${commentId}`, {
        status: 'resolved'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh comments
      const res = await axios.get(`/api/comments?reference_id=${referenceId}&table=${table}`);
      const allComments = res.data.comments || [];
      if (isFlagInterface) {
        setComments(allComments.filter(c => c.type === 'Flag' || c.type === 'Flag-General'));
      } else {
        setComments(allComments.filter(c => c.type !== 'Flag' && c.type !== 'Flag-General'));
      }
    } catch (err) {
      setError('Failed to resolve flag');
    }
  };

  if (!isOpen) return null;

  return (
    <div style={modalOverlay}>
      <div style={modalBox}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>{isFlagInterface ? 'Flags' : 'Comments'}</h2>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>
        <div style={{ color: '#555', fontSize: 14, margin: '8px 0 16px 0' }}>
          {isFlagInterface 
            ? 'This is where you may flag, resolve and discuss directly with creator of the project'
            : 'Comment here to communicate with your instructor/student. This is also where Instructor can make an evaluation.'
          }
        </div>
        <div style={{ maxHeight: 300, overflowY: 'auto', margin: '16px 0', border: '1px solid #eee', borderRadius: 6, padding: 8, background: '#fafbfc' }}>
          {loading ? <div>Loading...</div> :
            comments.length === 0 ? <div style={{ color: '#888' }}>No comments yet.</div> :
              [...comments].sort((a, b) => new Date(b.createddate) - new Date(a.createddate)).map(c => {
                let borderColor = '#eee';
                let bgColor = '#fff';
                if (c.type === 'Warning') {
                  borderColor = '#e53935';
                  bgColor = '#ffeaea';
                } else if (c.type === 'Evaluation') {
                  borderColor = '#fbc02d';
                  bgColor = '#fffbe6';
                } else if (c.type === 'Compliment') {
                  borderColor = '#43a047';
                  bgColor = '#e8f5e9';
                } else if (c.type === 'Flag') {
                  borderColor = '#dc3545';
                  bgColor = '#ffeaea';
                } else if (c.type === 'Flag-General') {
                  borderColor = '#6c757d';
                  bgColor = '#f8f9fa';
                }
                let displayName = '';
                if (c.first_name || c.last_name) {
                  displayName = `${c.first_name || ''} ${c.last_name || ''}`.trim();
                } else if (!c.account && c.alt_account) {
                  try {
                    const alt = JSON.parse(c.alt_account);
                    displayName = `${alt.first_name || ''} ${alt.last_name || ''}`.trim() || alt.primary_email || 'Guest User';
                  } catch {
                    displayName = 'Guest User';
                  }
                } else {
                  displayName = c.account || 'Unknown';
                }
                return (
                  <div key={c.id} style={{ marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #eee' }}>
                    <div style={{
                      border: `2px solid ${borderColor}`,
                      background: bgColor,
                      borderRadius: 6,
                      padding: 10,
                      marginBottom: 2,
                      whiteSpace: 'pre-wrap',
                    }}>
                      <div dangerouslySetInnerHTML={{ __html: c.content }} />
                    </div>
                    <div style={{ color: '#888', fontSize: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <span>By {displayName}{c.relevantrole ? ` (${c.relevantrole})` : ''}</span>
                        <span>•</span>
                        <span>{dateTimeToMonDDYYYYWithTime(c.createddate)}</span>
                        {c.status === 'resolved' && (
                          <>
                            <span>•</span>
                            <span style={{ color: '#28a745', fontWeight: 500 }}>✅ Resolved</span>
                          </>
                        )}
                      </div>
                      
                      {/* Resolve button for Flag comments */}
                      {isFlagInterface && c.type === 'Flag' && (
                        <div>
                          {c.status === 'resolved' ? (
                            <span style={{ color: '#28a745', fontSize: 11, fontWeight: 500 }}>✅ Resolved by Admin</span>
                          ) : relevantRole === 'Admin' ? (
                            <button
                              onClick={() => handleResolveFlag(c.id)}
                              style={{
                                padding: '4px 8px',
                                fontSize: 11,
                                background: '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: 4,
                                cursor: 'pointer',
                                fontWeight: 500
                              }}
                            >
                              ✅ Resolve
                            </button>
                          ) : (
                            <span style={{ color: '#6c757d', fontSize: 11, fontStyle: 'italic' }}>
                              Not marked as resolved by Admin yet
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
          }
        </div>
        
        {/* Legend for comment types */}
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: '#888', fontWeight: 500 }}>Legend:</span>
          {isFlagInterface ? (
            <>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                <span style={{ width: 16, height: 8, background: '#f8f9fa', border: '1px solid #6c757d', borderRadius: 2, display: 'inline-block' }}></span> General
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                <span style={{ width: 16, height: 8, background: '#ffeaea', border: '1px solid #dc3545', borderRadius: 2, display: 'inline-block' }}></span> Flag
              </span>
            </>
          ) : (
            <>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                <span style={{ width: 16, height: 8, background: '#eee', border: '1px solid #ccc', borderRadius: 2, display: 'inline-block' }}></span> General
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                <span style={{ width: 16, height: 8, background: '#ffeaea', border: '1px solid #e53935', borderRadius: 2, display: 'inline-block' }}></span> Warning
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                <span style={{ width: 16, height: 8, background: '#fffbe6', border: '1px solid #fbc02d', borderRadius: 2, display: 'inline-block' }}></span> Evaluation
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                <span style={{ width: 16, height: 8, background: '#e8f5e9', border: '1px solid #43a047', borderRadius: 2, display: 'inline-block' }}></span> Compliment
              </span>
            </>
          )}
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 8 }}>
            <label style={{ fontWeight: 500, marginRight: 8 }}>Type:</label>
            <select
              value={newType}
              onChange={e => setNewType(e.target.value)}
              disabled={isStudent}
              style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc' }}
            >
              {(isFlagInterface ? FLAG_COMMENT_TYPES : COMMENT_TYPES)
                .filter(t => {
                  // Students can only see General
                  if (isStudent) return t.value === 'General';
                  // Project creators (Instructors) in Flag interface can only see General
                  if (isFlagInterface && relevantRole === 'Instructor') return t.value === 'Flag-General';
                  // Admins can see all types
                  return true;
                })
                .map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
            </select>
          </div>
          <div style={{ marginBottom: 8 }}>
            <style>
              {`
                .project-comments-quill .ql-editor {
                  height: 120px !important;
                  max-height: 120px !important;
                  min-height: 120px !important;
                }
              `}
            </style>
            <ReactQuill
              theme="snow"
              value={newComment}
              onChange={setNewComment}
              className="project-comments-quill"
              style={{ marginBottom: 8 }}
              placeholder="Enter your comment (rich text supported)"
            />
          </div>
                     {error && <div style={{ color: 'red', marginBottom: 4 }}>{error}</div>}
           <button type="submit" disabled={submitting || !newComment || newComment === '<p><br></p>'} style={submitBtn}>
             {submitting ? 'Submitting...' : (newType === 'Flag' ? 'Flag' : 'Add Comment')}
           </button>
        </form>
      </div>
    </div>
  );
};

const modalOverlay = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0,0,0,0.25)',
  zIndex: 9999,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const modalBox = {
  background: '#fff',
  borderRadius: 8,
  padding: 24,
  minWidth: 400,
  maxWidth: 500,
  boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
  position: 'relative',
};
const closeBtn = {
  background: 'none',
  border: 'none',
  fontSize: 28,
  fontWeight: 700,
  color: '#888',
  cursor: 'pointer',
  lineHeight: 1,
};
const submitBtn = {
  padding: '8px 20px',
  background: '#1D4E89',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  fontWeight: 600,
  fontSize: 16,
  cursor: 'pointer',
};

export default ProjectComments; 